import { startOfDay, endOfDay } from 'date-fns'
import { z } from 'zod'

import type { IdAndName } from '@/types/id-and-name'
import { requiredString } from '@/utils/zod'

const lessonEvent = z.object({
  class_names: z.array(z.string()),
  co_teacher_1: z
    .object({ name: requiredString(), id: requiredString() })
    .nullable(),
  course: z.object({
    id: requiredString(),
    display_name: requiredString()
  }),
  end_date: z.string().datetime({ offset: true }),
  event_type: z.literal('lesson'),
  group: z.object({ id: requiredString() }).nullable(),
  id: requiredString(),
  room: z.object({ name: requiredString(), id: requiredString() }).nullable(),
  start_date: z.string().datetime({ offset: true }),
  teacher: z.object({ name: requiredString(), id: requiredString() }),
  teacher_cover: z
    .object({ name: requiredString(), id: requiredString() })
    .nullable(),
  tutors: z.array(z.string()),
  is_cancelled: z.boolean(),
  co_teacher_1_cover: z
    .object({ name: requiredString(), id: requiredString() })
    .nullable(),
  co_room: z.object({ id: z.string(), name: z.string() }).nullable()
})

const schoolProgrammmeEvent = z.object({
  id: z.union([z.string(), z.number()]),
  event_type: z.literal('school_programme'),
  start_date: z.string().datetime({ offset: true }),
  end_date: z.string().datetime({ offset: true }),
  title: requiredString(),
  room: z.object({ name: requiredString(), id: requiredString() }).nullable()
})

const schoolBreakEvent = z.object({
  id: requiredString(),
  event_type: z.literal('school_break'),
  start_date: z.string().date(),
  end_date: z.string().date(),
  name: requiredString()
})

export const responseSchema = z.array(
  z.union([lessonEvent, schoolProgrammmeEvent, schoolBreakEvent])
)

export type LessonEvent = {
  id: string
  startDate: Date
  endDate: Date
  classNames: string[]
  eventType: 'lesson'
  group: {
    id: string
  } | null
  course: {
    id: string
    displayName: string
  }
  room: IdAndName | null
  teacher: IdAndName
  teacherCover: IdAndName | null
  coTeacherCover: IdAndName | null
  coTeacher: IdAndName | null
  tutors: string[]
  isCancelled: boolean
  coRoom: IdAndName | null
}

export type SchoolProgrammeEvent = {
  id: string
  startDate: Date
  endDate: Date
  eventType: 'school_programme'
  room: IdAndName | null
  title: string
}

export type SchoolBreakEvent = {
  id: string
  startDate: Date
  endDate: Date
  eventType: 'school_break'
  name: string
}

export type TimetableEvent =
  | LessonEvent
  | SchoolProgrammeEvent
  | SchoolBreakEvent

export const parseResponse = (
  response: z.infer<typeof responseSchema>
): TimetableEvent[] =>
  response.map(item => {
    switch (item.event_type) {
      case 'lesson':
        return {
          id: item.id,
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          classNames: item.class_names,
          eventType: item.event_type,
          group: item.group,
          course: {
            id: item.course.id,
            displayName: item.course.display_name
          },
          room: item.room,
          teacher: item.teacher,
          teacherCover: item.teacher_cover,
          coTeacherCover: item.co_teacher_1_cover,
          coTeacher: item.co_teacher_1,
          tutors: item.tutors,
          isCancelled: item.is_cancelled,
          coRoom: item.co_room
            ? {
                id: item.co_room.id,
                name: item.co_room.name
              }
            : null
        }
      case 'school_break':
        return {
          id: item.id,
          startDate: startOfDay(new Date(item.start_date)),
          endDate: endOfDay(new Date(item.end_date)),
          eventType: item.event_type,
          name: item.name
        }
      case 'school_programme':
        return {
          id: item.id.toString(),
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          eventType: item.event_type,
          room: item.room,
          title: item.title
        }
    }
  })

export type Params = {
  dateFrom: string
  dateTo: string
  student?: string
  teacher?: string
  course?: string
  classId?: string
  room?: string
}
